import { useEffect, useRef, useState } from "react";
import type { Strings } from "../i18n/strings";
import type { Lang } from "../lib/types";
import { preloadCrumple, playCrumpleSound } from "../lib/sound";
import { hapticCrumple } from "../lib/native";
import Logo from "./Logo";

const LANGS: Lang[] = ["ko", "en", "ja"];

// ③ 구겨 버리기: 상단 로고 바 + 하단 버튼은 그대로 유지되고, 가운데 종이(영상)만 구겨져 나간다.
// 영상은 화면블렌드(screen)로 검은 배경 위 합성. 구김 소리는 영상 자체 오디오 사용(소리 토글 연동).
export default function Crumple({ t, lang, onDone }: { t: Strings; lang: Lang; onDone: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // 영상은 실제 재생이 시작될 때까지 투명 → 로드~재생 사이 WebView가 띄우는 재생버튼 플래시를 가린다.
  const [playing, setPlaying] = useState(false);
  const playedRef = useRef(false);

  useEffect(() => {
    hapticCrumple();
    preloadCrumple(); // 추출 오디오 미리 디코딩
    const v = videoRef.current;
    const fallback = setTimeout(onDone, 3200);
    if (v) {
      v.muted = true; // 영상은 항상 음소거(자동재생·재생버튼 방지). 소리는 Web Audio로 재생.
      const onLoaded = () => {
        v.play().catch(() => {});
      };
      // 재생 시작 → 영상 드러냄 + 추출 오디오(구김+튕김) 재생(동기화, 1회만).
      const onPlaying = () => {
        setPlaying(true);
        if (!playedRef.current) {
          playedRef.current = true;
          playCrumpleSound();
        }
      };
      v.addEventListener("loadedmetadata", onLoaded);
      v.addEventListener("playing", onPlaying);
      v.addEventListener("ended", onDone);
      return () => {
        clearTimeout(fallback);
        v.removeEventListener("loadedmetadata", onLoaded);
        v.removeEventListener("playing", onPlaying);
        v.removeEventListener("ended", onDone);
      };
    }
    return () => clearTimeout(fallback);
  }, [onDone]);

  return (
    <div className="screen vent crumple-frame">
      {/* 가운데에서 종이가 구겨지는 영상 (뒤 레이어) */}
      <video
        ref={(el) => {
          videoRef.current = el;
          // 로드 전에 음소거를 '확실히' 설정(React muted 속성 타이밍 이슈 회피) → 음소거 자동재생 보장.
          if (el) {
            el.muted = true;
            el.defaultMuted = true;
          }
        }}
        className={`crumple-video ${playing ? "is-playing" : ""}`}
        src="/assets/crumple.mp4"
        autoPlay
        muted
        playsInline
        preload="auto"
      />

      {/* 유지되는 상단 크롬 (정적) */}
      <div className="vent-header chrome">
        <Logo lang={lang} height={20} stroke={30} />
        <div className="vent-tools">
          <div className="lang-toggle-row">
            {LANGS.map((L, i) => (
              <span key={L}>
                {i > 0 && <span className="sep">/</span>}
                <span className={`lang-opt ${lang === L ? "on" : ""}`}>{L.toUpperCase()}</span>
              </span>
            ))}
          </div>
          <span className="gear">⚙</span>
        </div>
      </div>

      <div className="crumple-gap" />

      {/* 유지되는 하단 크롬 (정적) */}
      <p className="reassure chrome">{t.reassure}</p>
      <div className="mic chrome">🎤 {t.micHint}</div>
      <div className="submit-btn chrome">{t.submit}</div>
    </div>
  );
}
